/**
 * Aplica meta tags SEO / Open Graph / Twitter en el documento.
 * Los nodos creados llevan data-seo-managed="1" para poder reemplazarlos en cada actualización.
 */

function removeManagedNodes() {
  document.querySelectorAll('[data-seo-managed="1"]').forEach((n) => n.remove())
}

/** Evita duplicar meta del index.html estático al aplicar SEO desde API. */
function removeStaticSeoDuplicates() {
  document.querySelectorAll('meta[name="description"]:not([data-seo-managed])').forEach((n) => n.remove())
  document.querySelectorAll('meta[name="keywords"]:not([data-seo-managed])').forEach((n) => n.remove())
}

function appendMeta(attrs) {
  const m = document.createElement('meta')
  m.setAttribute('data-seo-managed', '1')
  Object.entries(attrs).forEach(([k, v]) => {
    if (v != null && v !== '') m.setAttribute(k, String(v))
  })
  document.head.appendChild(m)
}

function appendLink(rel, href) {
  if (!href) return
  const l = document.createElement('link')
  l.setAttribute('data-seo-managed', '1')
  l.setAttribute('rel', rel)
  l.setAttribute('href', href)
  document.head.appendChild(l)
}

function appendJsonLd(id, json) {
  const old = document.getElementById(id)
  if (old) old.remove()
  const s = document.createElement('script')
  s.type = 'application/ld+json'
  s.id = id
  s.setAttribute('data-seo-managed', '1')
  s.textContent = JSON.stringify(json)
  document.head.appendChild(s)
}

/**
 * @param {object} seo - datos SEO devueltos por backend
 * @param {{ pageLabel?: string, pathname?: string }} [opts]
 */
export function applySeoToDocument(seo, opts = {}) {
  if (!seo || typeof seo !== 'object') return

  removeManagedNodes()
  removeStaticSeoDuplicates()

  const pathname = opts.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/')
  const base =
    (seo.canonical_base_url && String(seo.canonical_base_url).replace(/\/$/, '')) ||
    (typeof window !== 'undefined' ? window.location.origin : '')
  const pathNorm = pathname.startsWith('/') ? pathname : `/${pathname}`
  const canonicalUrl = `${base}${pathNorm}`

  const pageLabel = opts.pageLabel
  const siteName = seo.site_name || 'Artemis'
  if (pageLabel) {
    document.title = `${pageLabel} | ${siteName}`
  } else {
    document.title = seo.default_title || siteName
  }

  const description = seo.meta_description || ''
  const ogTitle = seo.og_title || document.title
  const ogDescription = seo.og_description || description

  appendMeta({ name: 'description', content: description })
  if (seo.meta_keywords) {
    appendMeta({ name: 'keywords', content: seo.meta_keywords })
  }
  appendMeta({ name: 'robots', content: seo.robots || 'index, follow' })
  if (seo.theme_color) {
    appendMeta({ name: 'theme-color', content: seo.theme_color })
  }

  appendMeta({ property: 'og:type', content: seo.og_type || 'website' })
  appendMeta({ property: 'og:site_name', content: siteName })
  appendMeta({ property: 'og:title', content: ogTitle })
  appendMeta({ property: 'og:description', content: ogDescription })
  appendMeta({ property: 'og:url', content: canonicalUrl })
  appendMeta({ property: 'og:locale', content: seo.og_locale || 'es_ES' })
  if (seo.og_image_url) {
    appendMeta({ property: 'og:image', content: seo.og_image_url })
    appendMeta({ property: 'og:image:alt', content: ogTitle })
  }

  appendMeta({ name: 'twitter:card', content: seo.twitter_card || 'summary_large_image' })
  if (seo.twitter_site) {
    const tw = String(seo.twitter_site).trim()
    if (tw) appendMeta({ name: 'twitter:site', content: tw.startsWith('@') ? tw : `@${tw}` })
  }
  appendMeta({ name: 'twitter:title', content: ogTitle })
  appendMeta({ name: 'twitter:description', content: ogDescription })
  if (seo.og_image_url) {
    appendMeta({ name: 'twitter:image', content: seo.og_image_url })
  }

  appendLink('canonical', canonicalUrl)

  if (seo.include_organization_json_ld !== false) {
    const org = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: siteName,
      url: base || canonicalUrl,
    }
    if (seo.og_image_url) {
      org.logo = seo.og_image_url
    }
    appendJsonLd('seo-organization-jsonld', org)
  }
}
